/** @module Review: warns when the chosen card does not carry the number read from the photo. */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api, problemFrom } from "@/api/client";

async function fetchCheck(scanId: string, printId: string) {
  const { data, response } = await api.GET("/api/v1/scans/{scan_id}/number-check", {
    params: { path: { scan_id: scanId }, query: { print_id: printId } },
  });
  if (!data) throw problemFrom(response);
  return data;
}

/**
 * Reprints share the artwork, so the wrong set is easy to pick; the number on the card is not.
 * The backend decides whether it matches; this only shows the answer.
 */
export function NumberWarning({
  scanId,
  printId,
  onSwitch,
}: {
  scanId: string;
  printId: string | null;
  onSwitch: (printId: string) => void;
}) {
  const { t } = useTranslation();
  const check = useQuery({
    queryKey: ["number-check", scanId, printId],
    queryFn: () => fetchCheck(scanId, printId ?? ""),
    enabled: Boolean(printId),
  });

  const data = check.data;
  if (!data?.read || data.matches === null) return null;
  if (data.matches) {
    return (
      <p className="text-[length:var(--text-sm)] text-(--text-muted)">
        {t("review.numberCheck.match", { read: data.read })}
      </p>
    );
  }
  const suggestion = data.suggestion;
  return (
    <div
      role="alert"
      data-testid="number-warning"
      className="flex flex-wrap items-center gap-3 rounded-[var(--radius-sm)] border border-(--loss) px-3 py-2 text-[length:var(--text-sm)]"
    >
      <span>{t("review.numberCheck.mismatch", { read: data.read })}</span>
      {suggestion ? (
        <button
          type="button"
          onClick={() => {
            onSwitch(suggestion.print_id);
          }}
          className="min-h-(--touch) rounded-[var(--radius-sm)] border border-(--accent) px-3 text-(--accent)"
        >
          {t("review.numberCheck.switch", {
            name: suggestion.name ?? suggestion.print_id,
            set: suggestion.set_code ?? "",
            number: suggestion.local_id,
          })}
        </button>
      ) : null}
    </div>
  );
}
